import ModalComponent from "..";
import { useGetSinglePayoutDetailsQuery } from "../../../redux/features/payment/payment.api";
import { SinglePayout } from "../../../redux/features/payment/payment.types";
import { FEE_PERCENTAGE_TEXT, PRIMARY_COLOR } from "../../../utils/constants";
import { formatDate, formatToNaira } from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { Icon } from "../../icon/icon";
import { LoadingOval } from "../../loaders/loaders";
import { Pill } from "../../pill";
import { TableComponent } from "../../table";
import { MiniHeader } from "../../typography/miniHeader";

export const ViewPayoutModal = ({
  open,
  handleClose,
  payout,
  eventId,
  adminView = false,
}: ModalCompProps & {
  payout: SinglePayout;
  eventId: string;
  adminView?: boolean;
}) => {
  const { data: payoutDetails, isFetching: fetchingPayoutDetails } =
    useGetSinglePayoutDetailsQuery(
      {
        id: payout.status === "paid" && payout._id ? payout._id : eventId,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      containerClassName="md:!py-[20px] md:!px-[20px] md:!justify-end"
      className="!max-h-[90%] !rounded-t-[16px] !bg-white !p-[20px] md:!max-h-[100%] md:!max-w-[500px] md:!rounded-[16px]"
      dialogClassName="!my-0"
      showHeader
      mobileAnchorBottom
      closeOnOverlayClick
      fullScreen
      showCloseIcon
      showMobileTopBar={false}
    >
      {fetchingPayoutDetails ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingOval color={PRIMARY_COLOR} />
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-center justify-center gap-[2px] text-center">
            <Pill
              text={
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? "PENDING"
                  : "PAID"
              }
              variation={
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? "yellow"
                  : "green"
              }
            />

            <p className="text-2xl font-bold text-grey-900">
              {formatToNaira(payoutDetails?.data?.payoutInfo?.amount ?? 0)}
            </p>

            <p className="text-grey-500">
              {payoutDetails?.data?.payoutInfo?.status === "pending"
                ? "To be paid on"
                : "Paid on"}{" "}
              {formatDate(
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? payoutDetails?.data?.payoutInfo?.payoutDate
                  : payoutDetails?.data?.payoutInfo?.payoutDate,
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? "DD MMM, YYYY"
                  : "DD MMM, YYYY • hh:mm A",
              )}
            </p>
          </div>

          <TableComponent
            headers={[]}
            rows={[
              {
                id: 1,
                items: [
                  <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex flex-1 flex-col gap-[2px]">
                      <p className="font-bold text-primary">
                        {payout?.payoutAccount?.accountName}
                      </p>
                      <p className="text-grey-500">
                        {payout?.payoutAccount?.accountNumber} ∙{" "}
                        {payout?.payoutAccount?.bankName}
                      </p>
                    </div>

                    <Icon
                      svg="bank"
                      width={20}
                      height={20}
                      className="text-grey-400"
                    />
                  </div>,
                ],
              },
              {
                id: 2,
                items: [
                  <div className="flex flex-1 flex-col gap-[2px]">
                    <p className="text-grey-500">Tickets Sold</p>
                    <p className="font-bold text-primary">
                      {payoutDetails?.data?.payoutInfo?.totalTicketsSold}{" "}
                      {payoutDetails?.data?.payoutInfo?.totalTicketsSold === 1
                        ? "Ticket"
                        : "Tickets"}{" "}
                      ∙{" "}
                      {formatToNaira(
                        payoutDetails?.data?.payoutInfo?.totalTicketValue ?? 0,
                      )}
                    </p>
                  </div>,
                ],
              },
              {
                id: 3,
                items: [
                  <div className="flex flex-col gap-[2px]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-grey-500">Our Total Fee</p>

                      <Pill
                        text={
                          payoutDetails?.data?.payoutInfo?.description ??
                          `${FEE_PERCENTAGE_TEXT} PER TICKET`
                        }
                        variation="grey"
                      />
                    </div>

                    <p className="font-bold text-primary">
                      {formatToNaira(
                        payoutDetails?.data?.payoutInfo?.totalFees ?? 0,
                      )}
                    </p>
                  </div>,
                ],
              },
            ]}
            onRowClick={() => {}}
            isRowClickable={false}
            isTableEmpty={false}
            className="mt-10"
          />

          <div className="mt-5 flex flex-col gap-2">
            <MiniHeader header="History" />

            <TableComponent
              headers={[]}
              rows={
                payoutDetails?.data?.ticketSales?.map((sale, idx) => {
                  return {
                    id: idx,
                    items: [
                      <div
                        key={`${idx}`}
                        className="flex flex-col gap-[2px] text-sm text-grey-600"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="">
                            <span className="font-bold text-grey-900">
                              {formatToNaira(sale.ticketPrice * sale.quantity)}
                            </span>{" "}
                            ∙{" "}
                            <span>
                              {sale.quantity}x {sale.ticketName}
                            </span>
                          </p>

                          <p>
                            {formatDate(
                              sale.purchaseDate,
                              "DD MMM, YYYY • hh:mm A",
                            )}
                          </p>
                        </div>

                        <p>-{formatToNaira(sale.fee * sale.quantity)} Fee</p>
                      </div>,
                    ],
                  };
                }) ?? []
              }
              onRowClick={() => {}}
              isRowClickable={false}
              isTableEmpty={false}
            />
          </div>
        </>
      )}
    </ModalComponent>
  );
};
